import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { formatDateTime, formatNumber } from '../../../shared/format.js';
import { fieldErrors } from '../../../shared/api.js';
import { PlatformModal, Field, Row } from './PlatformModal.jsx';
import { StatusPill } from './StatusPill.jsx';
import { CopyBtn } from './CopyBtn.jsx';
import { useCompany, useUpdateCompany } from '../usePlatformCompanies.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function Section({ title, children, action }) {
  return (
    <div className="rounded-[10px] border border-line p-3">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[12.5px] font-bold flex-1">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Usage({ usage }) {
  if (!usage) {
    return (
      <div className="text-[12px] text-ink-3">
        لم تُهيّأ قاعدة بيانات هذه الشركة بعد — لا توجد مؤشرات استخدام.
      </div>
    );
  }
  const cells = [
    { label: 'المستخدمون', value: formatNumber(usage.users || 0) },
    { label: 'الجلسات', value: formatNumber(usage.sessions || 0) },
    { label: 'الفواتير', value: formatNumber(usage.invoices || 0) }
  ];
  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {cells.map((c) => (
          <div key={c.label} className="rounded-[10px] bg-paper-2 p-2.5 text-center">
            <div className="text-[11px] text-ink-3">{c.label}</div>
            <div className="text-[18px] font-extrabold mt-0.5">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="text-[11.5px] text-ink-3 mt-2">
        آخر نشاط: {usage.lastActivityAt ? formatDateTime(usage.lastActivityAt) : 'لا يوجد'}
      </div>
    </>
  );
}

function History({ company }) {
  const items = [
    company.createdAt && {
      label: 'الإنشاء',
      at: company.createdAt,
      by: company.createdByName,
      tone: '#6B7280'
    },
    company.provisionedAt && { label: 'تهيئة قاعدة البيانات', at: company.provisionedAt, tone: '#0E7490' },
    company.approvedAt && {
      label: 'الموافقة والتفعيل',
      at: company.approvedAt,
      by: company.approvedByName,
      tone: '#15803D'
    },
    company.rejectedAt && {
      label: 'الرفض',
      at: company.rejectedAt,
      reason: company.rejectReason,
      tone: '#B91C1C'
    },
    company.suspendedAt && {
      label: 'الإيقاف',
      at: company.suspendedAt,
      reason: company.suspendReason,
      tone: '#B91C1C'
    },
    company.lastLoginAt && { label: 'آخر دخول للشركة', at: company.lastLoginAt, tone: '#C2570D' }
  ].filter(Boolean);

  if (!items.length) return <div className="text-[12px] text-ink-3">لا يوجد سجل بعد.</div>;

  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div key={it.label} className="flex items-start gap-2 text-[12.5px]">
          <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: it.tone }} />
          <div className="flex-1">
            <div className="font-semibold">
              {it.label}
              {it.by ? <span className="text-ink-3 font-normal"> — بواسطة {it.by}</span> : null}
            </div>
            <div className="text-[11.5px] text-ink-3">{formatDateTime(it.at)}</div>
            {it.reason ? <div className="text-[11.5px] mt-0.5">السبب: {it.reason}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CompanyDetailsModal({ companyId, fallback, onClose }) {
  const detailsQ = useCompany(companyId);
  const company = detailsQ.data?.company || fallback || null;
  const usage = detailsQ.data?.usage || null;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', ownerFullName: '', ownerPhone: '', ownerEmail: '', notes: '' });
  const [serverErrors, setServerErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const update = useUpdateCompany({
    onSuccess: () => { setEditing(false); setSubmitted(false); setServerErrors({}); },
    onError: (err) => setServerErrors(fieldErrors(err))
  });

  useEffect(() => {
    if (!editing || !company) return;
    setForm({
      name: company.name || '',
      ownerFullName: company.owner?.fullName || '',
      ownerPhone: company.owner?.phone || '',
      ownerEmail: company.owner?.email || '',
      notes: company.notes || ''
    });
    setServerErrors({});
    setSubmitted(false);
  }, [editing, company?._id]);

  const errors = {};
  if (!form.name.trim()) errors.name = 'اسم الشركة مطلوب';
  if (!form.ownerFullName.trim()) errors.ownerFullName = 'اسم المالك مطلوب';
  if (form.ownerEmail.trim() && !EMAIL_RE.test(form.ownerEmail.trim())) errors.ownerEmail = 'بريد إلكتروني غير صحيح';
  const show = (key) => (submitted ? errors[key] : undefined) || serverErrors[key];

  function save() {
    setSubmitted(true);
    setServerErrors({});
    if (Object.keys(errors).length || update.isPending || !company) return;
    const current = {
      name: company.name || '',
      ownerFullName: company.owner?.fullName || '',
      ownerPhone: company.owner?.phone || '',
      ownerEmail: company.owner?.email || '',
      notes: company.notes || ''
    };
    const body = {};
    for (const key of Object.keys(current)) {
      const next = form[key].trim();
      if (next !== current[key]) body[key] = next;
    }
    if (!Object.keys(body).length) { setEditing(false); return; }
    update.mutate({ id: company._id, body });
  }

  if (!company) {
    return (
      <PlatformModal title="تفاصيل الشركة" onClose={onClose} width={640}>
        <div className="text-center py-10 text-ink-3">
          {detailsQ.isError ? 'تعذّر تحميل بيانات الشركة' : 'جاري التحميل...'}
        </div>
      </PlatformModal>
    );
  }

  return (
    <PlatformModal
      title={company.name}
      sub={'كود الشركة: ' + company.slug}
      onClose={onClose}
      width={640}
      footer={editing ? (
        <>
          <button type="button" className="btn flex-1 justify-center" onClick={() => setEditing(false)}>
            إلغاء التعديل
          </button>
          <button
            type="button"
            className="btn btn-primary flex-1 justify-center"
            onClick={save}
            disabled={update.isPending}
          >
            <Icon name="check" size={14} /> {update.isPending ? 'جاري الحفظ...' : 'حفظ التعديلات'}
          </button>
        </>
      ) : (
        <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إغلاق</button>
      )}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <StatusPill status={company.status} />
        <span className="font-mono text-[12.5px] text-ink-2" dir="ltr">{company.slug}</span>
        <CopyBtn value={company.slug} title="نسخ كود الشركة" />
        {detailsQ.isFetching ? <span className="text-[11.5px] text-ink-3">تحديث...</span> : null}
      </div>

      <Section
        title="بيانات الشركة"
        action={!editing ? (
          <button type="button" className="btn btn-sm" onClick={() => setEditing(true)}>
            <Icon name="edit" size={13} /> تعديل
          </button>
        ) : null}
      >
        {editing ? (
          <div className="space-y-3">
            <Field label="اسم الشركة" required error={show('name')}>
              <input
                className={clsx('input', show('name') && 'border-red')}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>
            <Field label="اسم المالك" required error={show('ownerFullName')}>
              <input
                className={clsx('input', show('ownerFullName') && 'border-red')}
                value={form.ownerFullName}
                onChange={(e) => setForm((f) => ({ ...f, ownerFullName: e.target.value }))}
              />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="هاتف المالك" error={show('ownerPhone')}>
                <input
                  className={clsx('input', show('ownerPhone') && 'border-red')}
                  value={form.ownerPhone}
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                  onChange={(e) => setForm((f) => ({ ...f, ownerPhone: e.target.value }))}
                />
              </Field>
              <Field label="بريد المالك" error={show('ownerEmail')}>
                <input
                  className={clsx('input', show('ownerEmail') && 'border-red')}
                  value={form.ownerEmail}
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                  onChange={(e) => setForm((f) => ({ ...f, ownerEmail: e.target.value }))}
                />
              </Field>
            </div>
            <Field label="ملاحظات" error={show('notes')}>
              <textarea
                className="input"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Field>
            <div className="text-[11.5px] text-ink-3">
              كود الشركة واسم مستخدم المالك غير قابلين للتعديل.
            </div>
          </div>
        ) : (
          <div>
            <Row label="اسم الشركة">{company.name}</Row>
            <Row label="كود الشركة">
              <span className="font-mono" dir="ltr">{company.slug}</span>
            </Row>
            <Row label="قاعدة البيانات">
              <span className="font-mono text-[11.5px]" dir="ltr">{company.dbName || 'غير مُهيّأة'}</span>
            </Row>
            <Row label="المالك">{company.owner?.fullName || '—'}</Row>
            <Row label="اسم مستخدم المالك">
              <span className="font-mono" dir="ltr">{company.owner?.username || '—'}</span>
            </Row>
            <Row label="الهاتف">
              <span dir="ltr">{company.owner?.phone || '—'}</span>
            </Row>
            <Row label="البريد الإلكتروني">
              <span dir="ltr">{company.owner?.email || '—'}</span>
            </Row>
            <Row label="ملاحظات">{company.notes || '—'}</Row>
          </div>
        )}
      </Section>

      <Section title="مؤشرات الاستخدام">
        <Usage usage={usage} />
      </Section>

      <Section title="سجل الحالة">
        <History company={company} />
      </Section>
    </PlatformModal>
  );
}
